import { useState, useCallback, useMemo } from 'react';

/**
 * useAIAssistance - Custom hook for AI-powered data collection assistance
 * 
 * Provides:
 * - Event category suggestions based on description
 * - Field auto-completion suggestions
 * - Similar incident pattern detection
 * - Data validation assistance
 * - Form completion guidance
 * 
 * Note: This is a mock implementation for data collection assistance only.
 * Real AI integration would connect to your preferred AI service.
 */

const useAIAssistance = (formData, lookupData) => {
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);

  // Mock AI patterns for event classification
  const eventPatterns = {
    'maintenance': {
      keywords: ['maintenance', 'repair', 'replace', 'service', 'inspect', 'check', 'fix', 'fault', 'malfunction'],
      category: 'Maintenance Error',
      types: ['Improper Maintenance', 'Missed Maintenance', 'Wrong Part Installation']
    },
    'ground_damage': {
      keywords: ['ground', 'damage', 'hit', 'collision', 'impact', 'scratch', 'dent', 'tug', 'pushback', 'ramp'],
      category: 'Ground Damage',
      types: ['Ground Support Equipment', 'Ramp Damage', 'Towing Incident']
    },
    'human_factors': {
      keywords: ['forgot', 'missed', 'error', 'mistake', 'confusion', 'misunderstand', 'rush', 'pressure', 'fatigue'],
      category: 'Human Factors',
      types: ['Communication Error', 'Procedural Error', 'Training Issue']
    },
    'equipment': {
      keywords: ['equipment', 'tool', 'machine', 'system', 'failure', 'breakdown', 'malfunction', 'defect'],
      category: 'Equipment Failure',
      types: ['Tool Malfunction', 'System Failure', 'Equipment Defect']
    },
    'safety': {
      keywords: ['safety', 'injury', 'hurt', 'accident', 'incident', 'unsafe', 'hazard', 'risk'],
      category: 'Safety Incident',
      types: ['Personal Injury', 'Unsafe Condition', 'Safety Violation']
    }
  };

  // Generate AI event title from description
  const generateEventTitle = useCallback((description, eventData = {}) => {
    if (!description || description.length < 20) return null;

    const text = description.toLowerCase();
    let title = '';

    // Extract key components
    const aircraft = eventData.aircraft_registration || '';
    const location = eventData.location_name || eventData.location_description || '';
    const category = eventData.event_category_name || '';

    // Pattern-based title generation
    if (text.includes('hydraulic') && text.includes('leak')) {
      title = `Hydraulic Fluid Leak${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    } else if (text.includes('maintenance') && (text.includes('error') || text.includes('mistake'))) {
      title = `Maintenance Error${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    } else if (text.includes('ground') && text.includes('damage')) {
      title = `Ground Damage Incident${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    } else if (text.includes('tool') && (text.includes('malfunction') || text.includes('failure'))) {
      title = `Tool Malfunction${location ? ` at ${location}` : ''}`;
    } else if (text.includes('spill') || text.includes('leak')) {
      const substance = text.includes('oil') ? 'Oil' : text.includes('fuel') ? 'Fuel' : text.includes('hydraulic') ? 'Hydraulic Fluid' : 'Fluid';
      title = `${substance} Spill${location ? ` at ${location}` : ''}`;
    } else if (text.includes('injury') || text.includes('hurt')) {
      title = `Personnel Injury${location ? ` at ${location}` : ''}`;
    } else if (text.includes('fire') || text.includes('smoke')) {
      title = `Fire/Smoke Incident${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    } else if (text.includes('collision') || text.includes('hit')) {
      title = `Collision Incident${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    } else {
      // Generic title based on category and key terms
      const keyWords = [];
      if (text.includes('maintenance')) keyWords.push('Maintenance');
      if (text.includes('equipment')) keyWords.push('Equipment');
      if (text.includes('safety')) keyWords.push('Safety');
      if (text.includes('inspection')) keyWords.push('Inspection');
      
      const baseTitle = keyWords.length > 0 ? keyWords.join('/') + ' Incident' : category || 'Safety Event';
      title = `${baseTitle}${aircraft ? ` - ${aircraft}` : ''}${location ? ` at ${location}` : ''}`;
    }

    // Ensure title is not too long
    if (title.length > 80) {
      const parts = title.split(' at ')[0]; // Remove location if too long
      title = parts.length <= 80 ? parts : parts.substring(0, 77) + '...';
    }

    return title || 'Safety Event Report';
  }, []);

  // Analyze text and suggest categories
  const analyzeEventDescription = useCallback((description) => {
    if (!description || description.length < 10) return null;

    const text = description.toLowerCase();
    const matches = {};
    
    // Score each pattern
    Object.entries(eventPatterns).forEach(([patternKey, pattern]) => {
      const matchCount = pattern.keywords.filter(keyword => text.includes(keyword)).length;
      if (matchCount > 0) {
        matches[patternKey] = {
          ...pattern,
          score: matchCount,
          confidence: Math.min(matchCount / pattern.keywords.length, 1.0)
        };
      }
    });

    // Find best match
    const sortedMatches = Object.entries(matches).sort(([,a], [,b]) => b.score - a.score);
    
    if (sortedMatches.length > 0) {
      const [bestPatternKey, bestMatch] = sortedMatches[0];
      
      // Find matching category and types from lookup data
      const suggestedCategory = lookupData.eventCategories?.find(cat => 
        cat.category_name.includes(bestMatch.category)
      );
      
      const suggestedTypes = lookupData.eventTypes?.filter(type => 
        type.category_id === suggestedCategory?.id && 
        bestMatch.types.some(patternType => type.type_name.includes(patternType))
      ) || [];

      return {
        pattern: bestPatternKey,
        confidence: bestMatch.confidence,
        suggestedCategory,
        suggestedTypes: suggestedTypes.slice(0, 3), // Top 3 suggestions
        reasoning: `Based on keywords: ${bestMatch.keywords.filter(k => text.includes(k)).join(', ')}`
      };
    }

    return null;
  }, [lookupData]);

  // Generate field suggestions based on context
  const generateFieldSuggestions = useCallback((fieldName, currentValue, context = {}) => {
    const suggestions = [];

    switch (fieldName) {
      case 'immediate_actions_taken':
        if (formData.injuries_occurred) {
          suggestions.push(
            'First aid administered',
            'Emergency services contacted',
            'Injured person removed from work area',
            'Area secured and cordoned off'
          );
        }
        if (formData.property_damage) {
          suggestions.push(
            'Damaged equipment isolated',
            'Area made safe',
            'Maintenance team notified',
            'Temporary barriers installed'
          );
        }
        if (formData.spill_occurred) {
          suggestions.push(
            'Spill contained with absorbent material',
            'Area ventilated',
            'Environmental team notified',
            'Cleanup initiated according to procedure'
          );
        }
        break;

      case 'potential_consequences':
        if (context.category === 'Maintenance Error') {
          suggestions.push(
            'Aircraft system malfunction during operation',
            'Unscheduled maintenance delay',
            'Potential flight safety impact',
            'Component damage or failure'
          );
        }
        if (context.category === 'Ground Damage') {
          suggestions.push(
            'Structural damage requiring repair',
            'Flight cancellation or delay',
            'Insurance claim required',
            'Aircraft out of service'
          );
        }
        break;

      case 'weather_conditions':
        const currentHour = new Date().getHours();
        if (currentHour >= 6 && currentHour <= 18) {
          suggestions.push('Clear daylight', 'Partly cloudy', 'Overcast');
        } else {
          suggestions.push('Clear night', 'Overcast night', 'Limited visibility');
        }
        suggestions.push('Light rain', 'Heavy rain', 'Windy conditions', 'High humidity');
        break;

      case 'equipment_involved':
        if (context.location?.includes('line') || context.location?.includes('ramp')) {
          suggestions.push(
            'Ground power unit',
            'Air start unit',
            'Baggage cart',
            'Fuel truck',
            'Pushback tug'
          );
        }
        if (context.location?.includes('hangar') || context.maintenance) {
          suggestions.push(
            'Hydraulic jack',
            'Work platform',
            'Engine hoist',
            'Test equipment',
            'Hand tools'
          );
        }
        break;
    }

    return suggestions.filter(s => s.toLowerCase().includes(currentValue?.toLowerCase() || ''));
  }, [formData]);

  // Main AI assistance function
  const getAIAssistance = useCallback(async (triggerField, currentFormData = formData) => {
    setLoading(true);
    
    try {
      // Simulate AI processing delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const assistance = {
        timestamp: new Date().toISOString(),
        suggestions: {},
        analysis: null,
        recommendations: []
      };

      // Event description analysis
      if (triggerField === 'event_description' && currentFormData.event_description) {
        const analysis = analyzeEventDescription(currentFormData.event_description);
        if (analysis) {
          assistance.analysis = analysis;
          
          // Category suggestion
          if (analysis.suggestedCategory && !currentFormData.event_category_id) {
            assistance.suggestions.event_category_id = {
              value: analysis.suggestedCategory.id,
              confidence: analysis.confidence,
              reasoning: analysis.reasoning
            };
          }
          
          // Type suggestions
          if (analysis.suggestedTypes.length > 0 && !currentFormData.event_type_id) {
            assistance.suggestions.event_type_id = {
              options: analysis.suggestedTypes.map(type => ({
                value: type.id,
                label: type.type_name,
                confidence: analysis.confidence * 0.8
              })),
              reasoning: 'Based on event description analysis'
            };
          }
        }
      }

      // Field-specific suggestions
      const contextualSuggestions = generateFieldSuggestions(
        triggerField, 
        currentFormData[triggerField],
        {
          category: assistance.analysis?.pattern,
          location: currentFormData.location_description,
          maintenance: currentFormData.maintenance_check_type
        }
      );

      if (contextualSuggestions.length > 0) {
        assistance.suggestions[triggerField] = {
          options: contextualSuggestions.map(suggestion => ({
            value: suggestion,
            confidence: 0.7
          })),
          reasoning: 'Based on context and similar incidents'
        };
      }

      // General recommendations
      const recommendations = [];
      
      if (currentFormData.injuries_occurred && !currentFormData.immediate_actions_taken) {
        recommendations.push({
          type: 'warning',
          message: 'Consider documenting immediate actions taken for injury response',
          field: 'immediate_actions_taken'
        });
      }
      
      if (currentFormData.property_damage && !currentFormData.estimated_damage_cost) {
        recommendations.push({
          type: 'info',
          message: 'Providing damage cost estimate helps with prioritization',
          field: 'estimated_damage_cost'
        });
      }
      
      if (currentFormData.is_near_miss && !currentFormData.near_miss_description) {
        recommendations.push({
          type: 'info',
          message: 'Near miss details help prevent similar incidents',
          field: 'near_miss_description'
        });
      }

      assistance.recommendations = recommendations;
      
      setSuggestions(assistance.suggestions);
      setLastAnalysis(assistance);
      
      return assistance;
      
    } catch (error) {
      console.error('AI assistance error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [formData, analyzeEventDescription, generateFieldSuggestions]);

  // Apply suggestion to form
  const applySuggestion = useCallback((fieldName, suggestionValue, updateFormData) => {
    updateFormData({ [fieldName]: suggestionValue });
    
    // Clear the applied suggestion
    setSuggestions(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  }, []);

  // Dismiss suggestion
  const dismissSuggestion = useCallback((fieldName) => {
    setSuggestions(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  }, []);

  // Get completion progress
  const completionAnalysis = useMemo(() => {
    const requiredFields = [
      'reporter_name', 'reporter_department_id', 'event_title', 
      'event_date_time', 'location_id', 'event_category_id', 
      'event_type_id', 'event_description'
    ];
    
    const completedRequired = requiredFields.filter(field => 
      formData[field] && (typeof formData[field] !== 'string' || formData[field].trim())
    ).length;
    
    const optionalFields = [
      'immediate_actions_taken', 'potential_consequences', 
      'weather_conditions', 'equipment_involved', 'other_personnel_involved'
    ];
    
    const completedOptional = optionalFields.filter(field => 
      formData[field] && formData[field].trim()
    ).length;
    
    const totalFields = requiredFields.length + optionalFields.length;
    const completedTotal = completedRequired + completedOptional;
    
    return {
      requiredComplete: completedRequired === requiredFields.length,
      requiredProgress: (completedRequired / requiredFields.length) * 100,
      overallProgress: (completedTotal / totalFields) * 100,
      missingRequired: requiredFields.filter(field => 
        !formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())
      ),
      suggestedNext: optionalFields.filter(field => 
        !formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())
      ).slice(0, 3)
    };
  }, [formData]);

  return {
    suggestions,
    loading,
    lastAnalysis,
    completionAnalysis,
    getAIAssistance,
    applySuggestion,
    dismissSuggestion,
    generateEventTitle
  };
};

export default useAIAssistance;
